import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../../Pagination';
import { api } from '../../../utils/api';
import { fmtDateTime, money, todayISO } from '../../../utils/region';
import {
  payrollRunsApi, PRR_MAX_TEXT, PRR_PAY_BOOKS, PRR_REMIT_KINDS,
  PrrBankLineBrief, PrrCashAccount, PrrPage, PrrPayBook, PrrPayRefusal, PrrRemitInput, PrrRemitKind, PrrRemitMonth, PrrRemitMonthKind, PrrRemitPreview,
  PrrRemitReversePreview, PrrRemittance,
} from '../../../utils/api/payrollRuns';
import type { AwCtx } from '../ctx';
import { card, dmy, errBox, note, num, pillAmber, pillGreen, pillGrey, pillRed, sectionHead, table, td, th } from '../ui';
import { Dialog, Foot, Loader, actBtn, emptyLine, field, lbl } from '../dialog';
import { DocEntriesBtn } from '../docEntries';
import { EntryLines } from './PayrollRuns';
import { bookWord, lineText, useAccountName } from './PayrollPayments';
import { TF, mmYyyy } from './payrollText';

// ── Payroll › Payroll List: remitting a month's PND.1 and SSO (batch 10 R8 — PAY-16; design §3.4.4, §4 R8; the R8 brief
// §3 package C task 6) ─────────────────────────────────────────────────────────────────────────────────────────────
// Above both of the Payroll List's views sits the month strip: one row per month of the shell's period that a posted run
// has given ภ.ง.ด.1 rows (or that has a remittance), newest first, and in it one cell per form — the amount the Filing
// Center's cell says as it reads NOW (R8-9: a row nobody typed is what the next seed writes), whether it has been
// remitted, and whether it has been filed. "Remitted" and "Filed" are two facts the screen never derives from each other
// (§4 R8): a month may be filed and not yet paid over, or paid over before the form is filed.
// Remit opens a card for the month: one or both kinds (R8-10 — one POST, a remittance and an entry each), the amount the
// Filing Center states and the payroll liability beside it, a reason where they differ, the account and book, the
// statement line of exactly the total, and one receipt number per kind (PND.1 is paid to the Revenue Department and SSO to
// the Social Security Office — each has its own receipt). The Remittances view lists them (100 a page); a remittance's
// window shows the runs it took its amount from and their shares, its entries (the house Entries window, kind
// payroll_remittance) and Reverse — which reverses every live remittance holding the same statement line (R8-10), so the
// card names each one before it is pressed.
// ⛔ The SERVER plans every remittance (services/payrollRemit.ts, package B): the months GET, the preview GET (always 200)
// and the POST run the same plan. Remit is drawn on a month only where a kind's own `ready` allows it and the seat may
// write; the card's Remit only on a preview of exactly what it sends that says ok. A refused non-GET pages the admins.
// ⛔ The strip's month goes stale under a person typing (or deleting) the cell in the Filing Center (R8 review L2): Remit
// reads the months again as it opens, and once a preview is in hand the card's figures — and whether a kind asks for a
// reason — are the preview's, the plan the POST runs again; closing the card reads the strip again.
// ⛔ The preview is asked only for a complete, well-formed remittance (a kind ticked, a date, an account, the bank book's
// volume when it is the bank book) — see PayrollPayments.tsx.
// ⛔ ภ.ง.ด.1 and สปส. are written here once each, as literals — never inside a translation.
// ⛔ Every jump goes through the pane's own ?prview / ?remit (replace) or ctx.setNav; nothing here writes ?menu / ?sub.

const FORM: Record<PrrRemitKind, string> = { pnd1: 'ภ.ง.ด.1', sso: 'สปส.' };
const formOf = (k?: string): string => (k === 'pnd1' || k === 'sso' ? FORM[k] : '');
const m2 = (sat: any): string => money((Number(sat) || 0) / 100);
const isIso = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));
const amberBox: React.CSSProperties = { border: '1px solid #F1C232', background: '#FFFBEB' };
const redBox: React.CSSProperties = { border: '1px solid #F3B4AE', background: '#FEF3F2' };
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontWeight: 700, fontSize: 12 };
const kv = (label: string, value: React.ReactNode, attr?: string) => (
  <div style={{ minWidth: 120 }} data-kv={attr}>
    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);
const ym = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;
/** One refusal of a remittance or of its reversal (payrollRemit.ts REMIT_REFUSALS ∪ REMIT_REVERSE_REFUSALS), in the
 *  screen's words: the form it names written as it is, the figures in money(). */
export function remitRefusalText(t: TF, r: PrrPayRefusal, accounts: PrrCashAccount[] = []): string {
  const k = `prr_rref_${r.code}`;
  const code = (id?: string) => accounts.find(a => a.id === id)?.code || '';
  const opts = {
    form: formOf(r.kind), filing: m2(r.filingSat), liability: m2(r.liabilitySat), line: m2(r.lineSat), total: m2(r.totalSat), date: r.date ? dmy(r.date) : '',
    account: code(r.pairedAccountId) || code(r.accountId), message: String(r.message || ''), docNos: (r.docNos || []).join(', '), rows: String(r.count ?? ''),
  };
  return t(k) !== k ? t(k, opts) : t('prr_rref_other');
}
/** An amber note beside a remittance (REMIT_ADVISORIES) — never blocking. */
function remitAdvText(t: TF, a: PrrPayRefusal): string {
  const k = `prr_radv_${a.code}`;
  return t(k) !== k ? t(k, { form: formOf(a.kind), docNos: (a.docNos || []).join(', ') }) : t('prr_radv_other');
}
/** The remittance routes' refusal of a write (REMIT_ERRORS). */
export function remitErr(e: any, t: TF): string {
  if (e?.readOnlySeat) return String(e.message || '');
  const p = e?.payload || {};
  const code = String(p.code || '');
  if (code === 'pay_forbidden') return t('prr_err_pay_forbidden');
  const k = `prr_remit_err_${code}`;
  if (code && t(k) !== k) return t(k, { field: String(p.field || ''), message: String(p.message || p.error || '') });
  return String(e?.message || '') || t('prr_err_generic');
}
function RemitRefusals({ refusals, t, accounts }: { refusals: PrrPayRefusal[]; t: TF; accounts?: PrrCashAccount[] }) {
  return (
    <div style={{ ...card, ...redBox, display: 'flex', flexDirection: 'column', gap: 5 }} data-remit-refusals={refusals.length}>
      {refusals.map((r, i) => (
        <div key={`${r.code}:${r.kind || ''}:${i}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, color: '#B42318' }} data-refusal={r.code} data-kind={r.kind || undefined}>
          <i className="ti ti-circle-x" aria-hidden /><span style={{ flex: 1 }} data-refusal-text>{remitRefusalText(t, r, accounts)}</span>
        </div>
      ))}
    </div>
  );
}
/** The eligible cash accounts (R8-25) — the server's list, read where it is answered: a posted run of the month's
 *  GET …/payment-options (§4.3 `accounts`). A client-level list, whichever run answers it; both reads are plain GETs. */
function useCashAccounts(ctx: AwCtx, y: number, m: number): PrrCashAccount[] | null {
  const [accounts, setAccounts] = useState<PrrCashAccount[] | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const list = await payrollRunsApi.list(ctx.clientId, { status: 'posted', periodId: ctx.periodId });
      const run = list.rows.find(r => r.year === y && r.month === m) || list.rows[0];
      const o = run ? await payrollRunsApi.paymentOptions(ctx.clientId, run.id) : null;
      if (alive) setAccounts(o?.accounts || []);
    })().catch(() => { if (alive) setAccounts([]); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, y, m]);
  return accounts;
}

// ── The month strip (above both views) ───────────────────────────────────────────────────────────────────────
// `active` is the list's own knowledge that the period holds a posted or reversed run: without one there is nothing a
// month could remit, and no read is made.
export function RemitMonthStrip({ ctx, active, readOnly, reloadKey, onRemit }: {
  ctx: AwCtx; active: boolean; readOnly: boolean; reloadKey: number; onRemit: (m: PrrRemitMonth) => void;
}) {
  const { t } = useTranslation();
  const [months, setMonths] = useState<PrrRemitMonth[] | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!active || !ctx.periodId) return;
    let alive = true;
    payrollRunsApi.remitMonths(ctx.clientId, ctx.periodId).then(d => { if (alive) { setMonths(d.months || []); setErr(''); } }).catch((e: any) => { if (alive) setErr(remitErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, ctx.reload, active, reloadKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!active) return null;
  if (err) return <div style={errBox} data-remit-strip-error>{err}</div>;
  if (!months || !months.length) return null;
  const cell = (k: PrrRemitKind, c: PrrRemitMonth['kinds'][PrrRemitKind]) => (
    <div key={k} style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap', minWidth: 300, flex: '1 1 300px' }}
      data-remit-cell={k} data-filing-sat={c.filingSat} data-remitted={c.remittance ? '1' : '0'} data-filed={c.filed ? '1' : '0'}>
      <b style={{ minWidth: 56 }}>{FORM[k]}</b>
      <span style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }} data-remit-amount>{m2(c.filingSat)}</span>
      <span style={c.remittance ? pillGreen : pillGrey} data-remit-chip={c.remittance ? 'remitted' : 'not_remitted'}>{t(c.remittance ? 'prr_strip_remitted' : 'prr_strip_not_remitted')}</span>
      <span style={c.filed ? pillGreen : pillGrey} data-filed-chip={c.filed ? 'filed' : 'not_filed'}>{t(c.filed ? 'prr_strip_filed' : 'prr_strip_not_filed')}</span>
      {c.filingEdited && <span style={pillAmber} data-filing-typed>{t('prr_strip_typed')}</span>}
      {c.diffSat !== 0 && <span style={{ fontSize: 11.5, color: '#92400E' }} data-remit-liability={c.liabilitySat}>{t('prr_strip_liability', { amount: m2(c.liabilitySat) })}</span>}
      {c.remittance && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }} data-remitted-amount={c.remittance.amountSat}>{t('prr_strip_remitted_on', { amount: m2(c.remittance.amountSat), date: dmy(c.remittance.payDate) })}</span>}
    </div>
  );
  return (
    <section style={{ ...card, display: 'flex', flexDirection: 'column', gap: 4 }} data-remit-strip={months.length}>
      <div style={sectionHead}>{t('prr_strip_head')}</div>
      {months.map(m => {
        const can = !readOnly && PRR_REMIT_KINDS.some(k => m.kinds[k]?.ready?.ok);
        return (
          <div key={ym(m.year, m.month)} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid var(--border)' }} data-remit-month={ym(m.year, m.month)}>
            <div style={{ minWidth: 120 }}>
              <b style={{ fontVariantNumeric: 'tabular-nums' }}>{mmYyyy(m.year, m.month)}</b>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{m.docNos.join(', ')}</div>
            </div>
            {PRR_REMIT_KINDS.map(k => (m.kinds[k] ? cell(k, m.kinds[k]) : null))}
            {can && <button style={{ ...actBtn, flex: 'none' }} onClick={() => onRemit(m)} data-remit-open={ym(m.year, m.month)}><i className="ti ti-building-bank" aria-hidden /> {t('prr_remit')}</button>}
          </div>
        );
      })}
    </section>
  );
}

// ── Remit (a card; its form the kinds, the reasons, the fields and the receipts) ─────────────────────────────────
/** What the card shows of a kind: the Filing Center's figure, what payroll owes, the difference, and where the figure is from. */
type RemitFigures = Pick<PrrRemitMonthKind, 'filingSat' | 'filingSource' | 'filingEdited' | 'liabilitySat' | 'diffSat'>;
export function RemitDialog({ ctx, month, onClose, onDone }: { ctx: AwCtx; month: PrrRemitMonth; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const acctName = useAccountName();
  const accounts = useCashAccounts(ctx, month.year, month.month);
  const [kinds, setKinds] = useState<Record<string, boolean>>(() => Object.fromEntries(PRR_REMIT_KINDS.map(k => [k, !!month.kinds[k]?.ready?.ok])));
  // The month as it reads now (R8 review L2): the strip's until the months GET answers. A kind that can no longer be
  // remitted is unticked (its tick is disabled below); one that now can be is left for the person to tick.
  const [cur, setCur] = useState<PrrRemitMonth>(month);
  useEffect(() => {
    let alive = true;
    payrollRunsApi.remitMonths(ctx.clientId, ctx.periodId).then(d => {
      const m = (d.months || []).find(x => x.year === month.year && x.month === month.month);
      if (!alive || !m) return;
      setCur(m);
      setKinds(x => Object.fromEntries(PRR_REMIT_KINDS.map(k => [k, !!x[k] && !!m.kinds[k]?.ready?.ok])));
    }).catch(() => { /* the strip's month stands; the preview still says what the POST would do */ });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, month.year, month.month]);
  // Each kind's figures from the latest preview (kept while the next one is asked, so a reason box never blinks out under
  // the typing); the month's before the first.
  const [figs, setFigs] = useState<Partial<Record<PrrRemitKind, RemitFigures>>>({});
  const figOf = (k: PrrRemitKind): RemitFigures | null => figs[k] || cur.kinds[k] || null;
  const asksReason = (k: PrrRemitKind): boolean => !!kinds[k] && (figOf(k)?.diffSat || 0) !== 0;
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [receipts, setReceipts] = useState<Record<string, string>>({});
  const [payDate, setPayDate] = useState(() => todayISO());
  const [book, setBook] = useState<PrrPayBook>('bank');
  const [accountId, setAccountId] = useState('');
  const [volume, setVolume] = useState('');
  const [volumes, setVolumes] = useState<string[]>([]);
  const [line, setLine] = useState<PrrBankLineBrief | null>(null);
  const [pv, setPv] = useState<{ key: string; p: PrrRemitPreview } | null>(null);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [changed, setChanged] = useState(false);
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  useEffect(() => {
    let alive = true;
    api.jnlBankAccounts(ctx.clientId).then((d: any) => {
      if (!alive) return;
      const list = (d?.accounts || []).map((a: any) => String(a.id)).filter(Boolean);
      setVolumes(list);
      setVolume(v => v || list[0] || '');
    }).catch(() => { if (alive) setVolumes([]); });
    return () => { alive = false; };
  }, [ctx.clientId]);
  const picked = PRR_REMIT_KINDS.filter(k => kinds[k]);
  const pick = <T,>(rec: Record<string, T>) => Object.fromEntries(picked.filter(k => !!rec[k]).map(k => [k, rec[k]]));
  const input: PrrRemitInput | null = !picked.length || !isIso(payDate) || !accountId || (book === 'bank' && !volume.trim()) ? null : {
    year: month.year, month: month.month, kinds: picked, filingNo: 0, payDate, sourceBook: book, accountId,
    ...(book === 'bank' ? { bankBookAccount: volume.trim() } : {}), ...(line ? { bankLineId: line.id } : {}),
    receiptRefs: pick(Object.fromEntries(Object.entries(receipts).map(([k, v]) => [k, v.trim()]))),
    // only where the card asks for one: a reason typed for a difference that has since gone is not sent
    diffReasons: pick(Object.fromEntries(Object.entries(reasons).filter(([k]) => asksReason(k as PrrRemitKind)).map(([k, v]) => [k, v.trim()]))),
  };
  const key = input ? JSON.stringify(input) : '';
  useEffect(() => {
    if (!key) return;
    let alive = true;
    const x = JSON.parse(key) as PrrRemitInput;
    const h = setTimeout(() => {
      payrollRunsApi.remitPreview(ctx.clientId, x).then(p => {
        if (!alive) return;
        setPv({ key, p }); setErr('');
        setFigs(f => ({ ...f, ...Object.fromEntries(p.kinds.map(q => [q.kind, {
          filingSat: q.filingSat, filingSource: q.filingSource, filingEdited: !!q.filingEdited, liabilitySat: q.liabilitySat, diffSat: q.diffSat,
        }])) }));
      }).catch((e: any) => { if (alive) setErr(remitErr(e, t)); });
    }, 300);
    return () => { alive = false; clearTimeout(h); };
  }, [ctx.clientId, key, reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  const p = pv && pv.key === key ? pv.p : null;
  const ready = !!input && !!p && p.ok && !busy;
  /** A statement line picked fills (and holds) the date, and the account when its bank account is paired (R8-5). */
  const pickLine = (b: PrrBankLineBrief | null) => {
    setDirty(true); setLine(b);
    if (!b) return;
    setPayDate(b.txnDate); setBook('bank');
    if (b.pairedAccountId) setAccountId(b.pairedAccountId);
  };
  const submit = async () => {
    if (!ready || !input) return;
    setBusy(true); setErr(''); setChanged(false);
    try {
      await payrollRunsApi.remit(ctx.clientId, input);
      setDirty(false); onDone();
    } catch (e: any) {
      if (e?.payload?.code === 'refused') setChanged(true); else setErr(remitErr(e, t));
      setPv(null); setReload(n => n + 1);
    } finally { setBusy(false); }
  };
  const label = mmYyyy(month.year, month.month);
  return (
    <Dialog width={760} title={t('prr_remit_title')} docNo={label} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-building-bank" label={t('prr_remit_go')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }} data-remit-form={ym(month.year, month.month)}>
        <div style={note}>{t('prr_remit_note')}</div>
        {changed && <div style={{ ...card, ...amberBox, fontSize: 12.5, color: '#92400E' }} data-remit-changed>{t('prr_pay_changed')}</div>}
        {err && <div style={errBox} data-remit-error>{err}</div>}
        {PRR_REMIT_KINDS.map(k => {
          const c = cur.kinds[k];
          const f = figOf(k);
          if (!c || !f) return null;
          const on = !!kinds[k];
          const deleted = c.ready.refusals.some(r => r.code === 'filing_deleted');
          return (
            <section key={k} style={{ ...card, display: 'flex', flexDirection: 'column', gap: 6 }} data-remit-kind={k}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, fontWeight: 700 }}>
                <input type="checkbox" checked={on} disabled={!c.ready.ok} onChange={ev => { setDirty(true); setKinds(x => ({ ...x, [k]: ev.target.checked })); }} data-remit-tick={k} />
                {FORM[k]}
              </label>
              {!c.ready.ok && <div style={{ fontSize: 12, color: '#92400E' }} data-remit-not-ready={k}>{c.ready.refusals.map(r => remitRefusalText(t, { ...r, kind: r.kind || k })).join(' · ')}</div>}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {kv(t('prr_remit_filing'), <span data-remit-filing={f.filingSat}>{m2(f.filingSat)}</span>, 'filing')}
                {kv(t('prr_remit_liability'), <span data-remit-liab={f.liabilitySat}>{m2(f.liabilitySat)}</span>, 'liability')}
                {kv(t('prr_remit_diff'), <span data-remit-diff={f.diffSat} style={f.diffSat ? { color: '#B45309' } : undefined}>{m2(f.diffSat)}</span>, 'diff')}
              </div>
              {!deleted && <div style={note}>{t(f.filingSource === 'live' ? 'prr_remit_src_live' : 'prr_remit_src_filing_center')}{f.filingEdited ? ` · ${t('prr_strip_typed')}` : ''}</div>}
              {asksReason(k) && (
                <div>
                  <label htmlFor={`prr-remit-reason-${k}`} style={lbl}>{t('prr_remit_reason')}</label>
                  <textarea id={`prr-remit-reason-${k}`} value={reasons[k] || ''} onChange={ev => { setDirty(true); setReasons(x => ({ ...x, [k]: ev.target.value })); }} rows={2} maxLength={PRR_MAX_TEXT}
                    style={{ ...field, ...(!String(reasons[k] || '').trim() ? amberBox : {}) }} data-remit-reason={k} />
                </div>
              )}
              {on && (
                <div>
                  <label htmlFor={`prr-remit-receipt-${k}`} style={lbl}>{t('prr_remit_receipt', { form: FORM[k] })}</label>
                  <input id={`prr-remit-receipt-${k}`} value={receipts[k] || ''} onChange={ev => { setDirty(true); setReceipts(x => ({ ...x, [k]: ev.target.value })); }} maxLength={PRR_MAX_TEXT} style={field} data-remit-receipt={k} />
                </div>
              )}
            </section>
          );
        })}
        <section style={{ ...card, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }} data-remit-fields>
          <div>
            <label htmlFor="prr-remit-date" style={lbl}>{t('prr_pay_f_date')}</label>
            <input id="prr-remit-date" type="date" value={payDate} disabled={!!line} onChange={ev => ed(setPayDate, ev.target.value)} style={field} data-remit-f="date" />
          </div>
          <div>
            <label htmlFor="prr-remit-book" style={lbl}>{t('prr_pay_f_book')}</label>
            <select id="prr-remit-book" value={book} disabled={!!line} onChange={ev => ed(setBook, ev.target.value as PrrPayBook)} style={field} data-remit-f="book">
              {PRR_PAY_BOOKS.map(b => <option key={b} value={b}>{t(`prr_pay_book_${b}`)}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="prr-remit-account" style={lbl}>{t('prr_pay_f_account')}</label>
            <select id="prr-remit-account" value={accountId} disabled={!!line?.pairedAccountId} onChange={ev => ed(setAccountId, ev.target.value)} style={field} data-remit-f="account">
              <option value="">{t('prr_choose')}</option>
              {(accounts || []).map(a => <option key={a.id} value={a.id}>{acctName(a)}</option>)}
            </select>
          </div>
          {book === 'bank' && (
            <div>
              <label htmlFor="prr-remit-volume" style={lbl}>{t('prr_pay_f_volume')}</label>
              <input id="prr-remit-volume" list="prr-remit-volumes" value={volume} onChange={ev => ed(setVolume, ev.target.value)} maxLength={PRR_MAX_TEXT} placeholder={t('prr_pay_volume_ph')} style={field} data-remit-f="volume" />
              <datalist id="prr-remit-volumes">{volumes.map(v => <option key={v} value={v} />)}</datalist>
            </div>
          )}
          <div style={{ gridColumn: '1 / -1' }} data-remit-bank-line>
            <div style={lbl}>{t('prr_pay_f_line')}</div>
            {line ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }} data-remit-line-picked={line.id}>
                <i className="ti ti-lock" aria-hidden /> <span>{lineText(line)}</span>
                <button style={linkBtn} onClick={() => pickLine(null)} data-remit-line-drop>{t('prr_pay_line_drop')}</button>
                <div style={{ ...note, width: '100%' }}>{t(line.pairedAccountId ? 'prr_pay_line_locked_account' : 'prr_pay_line_locked')}</div>
              </div>
            ) : book !== 'bank' ? <div style={note}>{t('prr_pay_line_bank_only')}</div>
              : !p ? <div style={note}>{t('prr_pay_line_wait')}</div>
              : !(p.bankLines?.rows || []).length ? <div style={note} data-remit-no-lines>{t('prr_pay_line_none', { amount: m2(p.totalSat) })}</div> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} data-remit-line-offers={(p.bankLines?.rows || []).length}>
                  {(p.bankLines?.rows || []).map(b => (
                    <label key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5 }}>
                      <input type="radio" name="prr-remit-line" checked={false} onChange={() => pickLine(b)} data-remit-line-offer={b.id} />
                      <span>{lineText(b)}</span>
                    </label>
                  ))}
                </div>
              )}
          </div>
        </section>
        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-remit-preview={p ? (p.ok ? 'ok' : 'refused') : ''}>
          {!input ? <div style={{ ...card, ...emptyLine }} data-remit-incomplete>{t('prr_remit_incomplete')}</div> : !p ? <div style={card}><Loader /></div> : (
            <>
              {p.refusals.length > 0 && <RemitRefusals refusals={p.refusals} t={t} accounts={accounts || []} />}
              {p.advisories.length > 0 && (
                <div style={{ ...card, ...amberBox, display: 'flex', flexDirection: 'column', gap: 4 }} data-remit-advisories={p.advisories.length}>
                  {p.advisories.map((a, i) => <div key={`${a.code}:${i}`} style={{ fontSize: 12.5, color: '#92400E' }} data-remit-advisory={a.code}><i className="ti ti-alert-triangle" aria-hidden /> {remitAdvText(t, a)}</div>)}
                </div>
              )}
              {p.kinds.map(x => (
                <div key={x.kind} style={card} data-remit-entry={x.kind} data-remit-amount-sat={x.amountSat}>
                  <div style={sectionHead}>{FORM[x.kind]} · {t('prr_remit_entry_head', { amount: m2(x.amountSat) })}</div>
                  {x.entry && (
                    <>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                        {dmy(x.entry.date)} · {bookWord(t, x.entry.book)}{x.entry.bankBookAccount ? ` · ${x.entry.bankBookAccount}` : ''} · {x.entry.docNo} — {x.entry.narration}
                      </div>
                      <EntryLines lines={x.entry.lines} totalSat={x.amountSat} t={t} />
                    </>
                  )}
                  {x.runs.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }} data-remit-shares={x.runs.length}>
                      {t('prr_remit_shares')}: {x.runs.map(r => `${r.docNo} ${r.code} ${m2(r.shareSat)}`).join(' · ')}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </section>
      </div>
    </Dialog>
  );
}

// ── The Remittances view (100 a page) ───────────────────────────────────────────────────────────────────────
export function RemittancesView({ ctx, reloadKey, onOpen }: { ctx: AwCtx; reloadKey: number; onOpen: (id: string) => void }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PrrPage<PrrRemittance> | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    payrollRunsApi.remittances(ctx.clientId, { periodId: ctx.periodId, page }).then(d => { if (alive) { setData(d); setErr(''); } }).catch((e: any) => { if (alive) setErr(remitErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, ctx.reload, page, reloadKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div style={errBox} data-remittances-error>{err}</div>;
  if (!data) return <Loader />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-remittances={data.total}>
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {!data.rows.length ? <div style={emptyLine} data-remittances-none>{t('prr_remittances_none')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('prr_rcol_month')}</th><th style={th}>{t('prr_rcol_kind')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('prr_rcol_amount')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prr_remit_filing')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('prr_remit_liability')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prr_remit_diff')}</th>
                <th style={th}>{t('prr_pcol_date')}</th><th style={th}>{t('prr_pcol_account')}</th><th style={th}>{t('prr_pcol_book')}</th><th style={th}>{t('prr_pcol_line')}</th>
                <th style={th}>{t('prr_rcol_receipt')}</th><th style={th}>{t('prr_pcol_state')}</th><th style={th}>{t('prr_pcol_by')}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.id} data-remittance={r.id} data-kind={r.kind} data-state={r.state} style={r.state === 'reversed' ? { color: 'var(--text-tertiary)' } : undefined}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button style={{ ...linkBtn, fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }} onClick={() => onOpen(r.id)} data-open-remittance={r.id}>{mmYyyy(r.year, r.month)}</button>
                  </td>
                  <td style={{ ...td, fontWeight: 700 }}>{formOf(r.kind)}</td>
                  <td style={{ ...num, fontWeight: 700 }} data-remittance-amount={r.amountSat}>{m2(r.amountSat)}</td>
                  <td style={num}>{m2(r.filingAmountSat)}{r.filingEdited ? <div><span style={pillAmber}>{t('prr_strip_typed')}</span></div> : null}</td>
                  <td style={num}>{m2(r.liabilitySat)}</td>
                  <td style={num}>{m2(r.diffSat)}{r.diffReason ? <div style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'normal' }}>{r.diffReason}</div> : null}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(r.payDate)}</td>
                  <td style={td}>{r.accountCode}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }} data-remittance-page={r.pageNo ?? ''}>
                    {bookWord(t, r.sourceBook)}{r.bankBookAccount ? ` · ${r.bankBookAccount}` : ''}
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.entryDocNo}{r.pageNo !== null && r.pageNo !== undefined ? ` · ${t('prr_pay_page', { page: r.pageNo })}` : ''}</div>
                  </td>
                  <td style={{ ...td, fontSize: 12 }}>{r.bankLine ? lineText(r.bankLine) : '—'}</td>
                  <td style={td}>{r.receiptRef || '—'}</td>
                  <td style={td}><span style={r.state === 'reversed' ? pillRed : pillGreen} data-remittance-state={r.state}>{t(r.state === 'reversed' ? 'prr_pstate_reversed' : 'prr_pstate_live')}</span></td>
                  <td style={{ ...td, fontSize: 12 }}>{r.createdByName}<div style={{ color: 'var(--text-tertiary)' }}>{r.createdAt ? fmtDateTime(r.createdAt) : ''}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Pagination page={page} total={data.total} limit={data.limit || 100} onPage={setPage} />
      <div style={note}>{t('prr_remittances_note')}</div>
    </div>
  );
}

// ── One remittance (a card, read-only): the runs and their shares, its entries, and Reverse ──────────────────────
export function RemittanceDialog({ ctx, remitId, readOnly, onClose, onReversed }: { ctx: AwCtx; remitId: string; readOnly: boolean; onClose: () => void; onReversed: () => void }) {
  const { t } = useTranslation();
  const [r, setR] = useState<PrrRemittance | null>(null);
  const [err, setErr] = useState('');
  const [reload, setReload] = useState(0);
  const [rev, setRev] = useState(false);
  useEffect(() => {
    let alive = true;
    payrollRunsApi.remittance(ctx.clientId, remitId).then(x => { if (alive) { setR(x); setErr(''); } }).catch((e: any) => { if (alive) setErr(remitErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, remitId, reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  const title = r ? `${formOf(r.kind)} · ${mmYyyy(r.year, r.month)}` : '';
  return (
    <Dialog width={720} title={t('prr_remittance_title')} docNo={title || undefined} onClose={onClose}>
      {err && <div style={{ ...errBox, marginBottom: 8 }} data-remittance-error>{err}</div>}
      {!r ? (err ? null : <Loader />) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-remittance-window={r.id} data-state={r.state}>
          <section style={{ ...card, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {kv(t('prr_rcol_amount'), <span data-remittance-amount={r.amountSat}>{m2(r.amountSat)}</span>, 'amount')}
            {kv(t('prr_remit_filing'), <span>{m2(r.filingAmountSat)}{r.filingEdited ? ` · ${t('prr_strip_typed')}` : ''}</span>, 'filing')}
            {kv(t('prr_remit_liability'), m2(r.liabilitySat), 'liability')}
            {kv(t('prr_remit_diff'), <span>{m2(r.diffSat)}{r.diffReason ? ` — ${r.diffReason}` : ''}</span>, 'diff')}
            {kv(t('prr_pcol_date'), dmy(r.payDate), 'date')}
            {kv(t('prr_pcol_account'), r.accountCode, 'account')}
            {kv(t('prr_pcol_book'), <span data-remittance-page={r.pageNo ?? ''}>{bookWord(t, r.sourceBook)}{r.bankBookAccount ? ` · ${r.bankBookAccount}` : ''} · {r.entryDocNo}{r.pageNo !== null && r.pageNo !== undefined ? ` · ${t('prr_pay_page', { page: r.pageNo })}` : ''}</span>, 'book')}
            {kv(t('prr_pcol_line'), r.bankLine ? lineText(r.bankLine) : '—', 'line')}
            {kv(t('prr_rcol_receipt'), r.receiptRef || '—', 'receipt')}
            {kv(t('prr_pcol_state'), <span style={r.state === 'reversed' ? pillRed : pillGreen} data-remittance-state={r.state}>{t(r.state === 'reversed' ? 'prr_pstate_reversed' : 'prr_pstate_live')}</span>, 'state')}
            {kv(t('prr_pcol_by'), `${r.createdByName}${r.createdAt ? ` · ${fmtDateTime(r.createdAt)}` : ''}`, 'by')}
          </section>
          {r.reversal && (
            <div style={{ ...card, fontSize: 12.5 }} data-remittance-reversal={r.reversal.docNo}>
              {t('prr_pay_reversed_by', { docNo: r.reversal.docNo, page: r.reversal.pageNo ?? '—' })}{r.reverseReason ? ` — ${r.reverseReason}` : ''}
              {r.reversedByName ? <span style={{ color: 'var(--text-tertiary)' }}> · {r.reversedByName}{r.reversedAt ? ` · ${fmtDateTime(r.reversedAt)}` : ''}</span> : null}
            </div>
          )}
          {r.sharesLineWith.length > 0 && <div style={note} data-remittance-shares-line>{t('prr_remit_shares_line', { forms: r.sharesLineWith.map(x => formOf(x.kind)).join(', ') })}</div>}
          <section style={card} data-remittance-runs={r.runs.length}>
            <div style={sectionHead}>{t('prr_remit_shares')}</div>
            <table style={table}>
              <tbody>
                {r.runs.map(x => (
                  <tr key={x.runId} data-remittance-run={x.runId}>
                    <td style={td}>
                      <button style={{ ...linkBtn, fontSize: 12.5 }} onClick={() => ctx.setNav('payroll', 'payroll_list', { run: x.runId, remit: '' })} data-remittance-open-run={x.runId}>{x.docNo}</button>
                    </td>
                    <td style={td}>{x.code}</td><td style={num}>{m2(x.shareSat)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span data-remittance-entries><DocEntriesBtn ctx={ctx} kind="payroll_remittance" id={r.id} docNo={`${formOf(r.kind)} ${mmYyyy(r.year, r.month)}`} /></span>
            {!readOnly && r.state === 'live' && (r.readiness?.reverse?.ok
              ? <button style={{ ...actBtn, color: '#B45309' }} onClick={() => setRev(true)} data-remittance-reverse={r.id}><i className="ti ti-arrow-back" aria-hidden /> {t('prr_pay_reverse')}</button>
              : <span style={{ fontSize: 12, color: '#92400E' }} data-remittance-reverse-refused>{(r.readiness?.reverse?.refusals || []).map(x => remitRefusalText(t, x)).join(' · ')}</span>)}
          </div>
        </div>
      )}
      {rev && r && <ReverseRemitDialog ctx={ctx} remittance={r} onClose={() => setRev(false)} onDone={() => { setRev(false); setReload(n => n + 1); onReversed(); }} />}
    </Dialog>
  );
}

// ── Reverse a remittance (a card; its form is the reason) ────────────────────────────────────────────────────
// Every live remittance holding the same statement line goes with it (R8-10) — the card names each one, with the
// reversing entries the server would post (dated as the originals, in their periods) and the line it frees.
export function ReverseRemitDialog({ ctx, remittance, onClose, onDone }: { ctx: AwCtx; remittance: PrrRemittance; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [pv, setPv] = useState<PrrRemitReversePreview | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  useEffect(() => {
    let alive = true;
    payrollRunsApi.remitReversePreview(ctx.clientId, remittance.id).then(x => { if (alive) { setPv(x); setErr(''); } }).catch((e: any) => { if (alive) setErr(remitErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, remittance.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  const ready = !!pv && pv.ok && !!reason.trim();
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await payrollRunsApi.reverseRemit(ctx.clientId, remittance.id, { reason: reason.trim() });
      setDirty(false); onDone();
    } catch (e: any) { setErr(remitErr(e, t)); } finally { setBusy(false); }
  };
  return (
    <Dialog width={600} title={t('prr_remit_reverse_title')} docNo={`${formOf(remittance.kind)} · ${mmYyyy(remittance.year, remittance.month)}`} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-arrow-back" label={t('prr_pay_reverse_go')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }} data-remit-reverse-form>
        <div style={note}>{t('prr_remit_reverse_note')}</div>
        {err && <div style={errBox} data-remit-reverse-error>{err}</div>}
        {!pv ? (err ? null : <Loader />) : (
          <>
            {pv.refusals.length > 0 && <RemitRefusals refusals={pv.refusals} t={t} />}
            <div style={{ ...card, fontSize: 12.5 }} data-remit-reverses={pv.reverses.length}>
              <div style={sectionHead}>{t('prr_remit_reverse_names')}</div>
              {pv.reverses.map(x => <div key={x.id} data-remit-reverses-one={x.kind}><b>{formOf(x.kind)}</b> · {mmYyyy(remittance.year, remittance.month)} · {m2(x.amountSat)}</div>)}
            </div>
            {pv.reversals.map((x, i) => (
              <section key={i} style={card} data-remit-reverse-entry={i}>
                <div style={sectionHead}>{t('prr_reverse_entry_head', { date: dmy(x.date) })}</div>
                <EntryLines lines={x.lines} totalSat={x.totalSat} t={t} />
              </section>
            ))}
            {pv.releasesLine && <div style={{ ...card, fontSize: 12.5 }} data-remit-reverse-frees={pv.releasesLine.id}>{t('prr_pay_reverse_frees', { line: lineText(pv.releasesLine) })}</div>}
          </>
        )}
        <label htmlFor="prr-remit-rev-reason" style={lbl}>{t('prr_reason')}</label>
        <textarea id="prr-remit-rev-reason" value={reason} onChange={ev => ed(setReason, ev.target.value)} rows={3} maxLength={PRR_MAX_TEXT} style={{ ...field, ...(!reason.trim() ? amberBox : {}) }} data-remit-reverse-reason />
      </div>
    </Dialog>
  );
}
